import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Calendar, CircleDot, Fuel, Gauge, MapPin, Star, Users } from "lucide-react";
import { useState } from "react";

import { BookingForm } from "@/components/site/BookingForm";
import { CarGrid } from "@/components/site/CarGrid";
import { LocationMap } from "@/components/site/LocationMap";
import { getPickupPoint } from "@/data/locations";
import { Badge } from "@/components/ui/badge";
import { cars, getCarBySlug, getCarFullModel } from "@/data/cars";
import { CarPhoto } from "@/components/site/CarPhoto";
import { carBestFor, carFaq, carHead, carLongDescription } from "@/lib/car-seo";
import { cn } from "@/lib/utils";
import { publicPageHeaders } from "@/lib/http-cache";
import { getCarAvailability } from "@/lib/availability.functions";
import { AvailabilityBanner } from "@/components/site/AvailabilityBanner";
import type { CarAvailability } from "@/lib/availability";

export const Route = createFileRoute("/rentals/$slug")({
  headers: publicPageHeaders,
  loader: async ({ params }) => {
    const car = getCarBySlug(params.slug);
    if (!car) throw notFound();
    let availability: CarAvailability | null = null;
    try {
      availability = await getCarAvailability({ data: { slug: car.slug } });
    } catch {
      availability = null;
    }
    return { car, availability };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Car unavailable — Car Rental Bucharest" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    return carHead(loaderData.car);
  },
  component: CarDetail,
});

function CarDetail() {
  const { car, availability } = Route.useLoaderData();
  const [active, setActive] = useState(0);

  const related = cars.filter((c) => c.brand === car.brand && c.id !== car.id).slice(0, 3);
  const modelName = getCarFullModel(car);
  const longDescription = carLongDescription(car);
  const bestFor = carBestFor(car);
  const faq = carFaq(car);

  const specs = [
    { icon: Gauge, label: "Transmission", value: car.transmission },
    { icon: Fuel, label: "Fuel type", value: car.fuelType },
    { icon: CircleDot, label: "Drive side", value: `${car.drive} hand drive` },
    { icon: Users, label: "Capacity", value: `${car.seats} seats` },
    { icon: Calendar, label: "Make year", value: String(car.year) },
    { icon: MapPin, label: "Body type", value: car.bodyType },
  ];

  return (
    <div className="container-page py-8 lg:py-12">
      <Link
        to="/rentals"
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" /> Back to all cars
      </Link>

      <div className="mt-6 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="min-w-0">
          <div className="photo-stage overflow-hidden rounded-2xl border border-border shadow-card">
            <CarPhoto
              src={car.images[active]}
              alt={`${car.brand} ${car.name.split(":")[0]} available for rent in Bucharest`}
              loading="eager"
              fetchPriority="high"
              sizes="(min-width: 1024px) 760px, 100vw"
              className="aspect-[3/2] w-full p-6"
            />
          </div>
          <div className={cn("mt-3 grid-cols-3 gap-3", car.images.length > 1 ? "grid" : "hidden")}>
            {car.images.map((img: string, i: number) => (
              <button
                key={i}
                type="button"
                aria-label={`View ${car.brand} photo ${i + 1}`}
                onClick={() => setActive(i)}
                className={cn(
                  "photo-stage overflow-hidden rounded-xl border-2 transition-colors",
                  i === active ? "border-gold" : "border-transparent hover:border-border",
                )}
              >
                <CarPhoto
                  src={img}
                  alt={`${car.brand} view ${i + 1}`}
                  sizes="200px"
                  className="aspect-[3/2] w-full p-2"
                />
              </button>
            ))}
          </div>


          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className="border border-gold/40 bg-ink/80 uppercase tracking-[0.14em] text-gold">{car.brand}</Badge>
              <span className="flex items-center gap-1 text-sm font-semibold">
                <Star className="size-4 fill-gold text-gold" /> {car.rating.toFixed(1)}
                <span className="font-normal text-muted-foreground">({car.reviews} reviews)</span>
              </span>
            </div>
            <h1 className="mt-4 text-2xl font-bold leading-tight sm:text-3xl">{car.name}</h1>
            <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="size-4 shrink-0 text-gold" /> {car.location}
            </p>
            <LocationMap point={getPickupPoint("main")} height="h-44" className="mt-4 max-w-md" />
          </div>

          <dl className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {specs.map((spec) => (
              <div
                key={spec.label}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4"
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
                  <spec.icon className="size-5" />
                </span>
                <div className="min-w-0">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                    {spec.label}
                  </dt>
                  <dd className="truncate font-display text-sm font-bold">{spec.value}</dd>
                </div>
              </div>
            ))}
          </dl>

          {availability ? <AvailabilityBanner availability={availability} className="mt-6" /> : null}

          <section className="mt-10">
            <h2 className="font-display text-xl font-bold">
              About the {car.year} {modelName}
            </h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">{longDescription}</p>
            <p className="mt-4 rounded-xl border border-gold/30 bg-card p-4 text-sm">
              <span className="font-semibold text-gold">Best for:</span>{" "}
              <span className="text-muted-foreground">{bestFor.use}.</span>
            </p>
          </section>
        </div>

        <div>
          <BookingForm car={car} availability={availability} />
        </div>
      </div>

      <section className="mt-16 max-w-3xl">
        <h2 className="font-display text-xl font-bold">
          {modelName} rental questions
        </h2>
        <dl className="mt-5 space-y-5">
          {faq.map((item) => (
            <div key={item.q} className="rounded-xl border border-border bg-card p-5">
              <dt className="font-display text-sm font-bold">{item.q}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12 max-w-3xl">
        <h2 className="font-display text-xl font-bold">Useful links</h2>
        <ul className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link to="/rentals" className="text-gold hover:underline">
              All {cars.length} cars for rent in Bucharest
            </Link>
          </li>
          <li>
            <Link to="/car-rental-no-deposit-bucharest" className="text-gold hover:underline">
              Car rental with no deposit in Bucharest
            </Link>
          </li>
          <li>
            <Link
              to="/rent-a-car-without-credit-card-bucharest"
              className="text-gold hover:underline"
            >
              Rent a car without a credit card
            </Link>
          </li>
          <li>
            <Link to="/good-to-know" className="text-gold hover:underline">
              Driving, parking and tolls in Bucharest
            </Link>
          </li>
          <li>
            <Link
              to="/blog/$slug"
              params={{ slug: "cheap-car-rental-bucharest-otopeni-airport" }}
              className="text-gold hover:underline"
            >
              Cheap car rental at Otopeni Airport
            </Link>
          </li>
          <li>
            <Link to="/blog" className="text-gold hover:underline">
              All rental guides
            </Link>
          </li>
        </ul>
      </section>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-xl font-bold">More {car.brand} cars</h2>
          <CarGrid cars={related} className="mt-6" />
        </section>
      )}
    </div>
  );
}
